import { useState } from 'react';
import { avatarVariants } from '@heroui/react';
import { cn } from '@/utils/cn';

/**
 * Person avatar — HeroUI's avatar styles, reduced to the one shape the app uses.
 *
 * Callers pass a name and optionally an image; the initial fallback is derived
 * here so every avatar in the product derives it the same way, instead of each
 * screen hand-rolling `name?.[0]?.toUpperCase()` into a styled div.
 *
 * Only `avatarVariants` is taken from HeroUI, not its `Avatar` component: that
 * one is built on `@radix-ui/react-avatar`, and the app keeps Radix out of the
 * bundle. The image sits over the initial and fades in once it loads; a broken
 * image is dropped so the initial shows, which is what the Radix version did.
 */
export type AvatarSize = 'sm' | 'md' | 'lg';

const PX: Record<AvatarSize, string> = {
  sm: 'size-8 text-small',
  md: 'size-10 text-body',
  lg: 'size-12 text-h3',
};

const slots = avatarVariants();

export interface AvatarProps {
  name?: string | null;
  src?: string | null;
  size?: AvatarSize;
  className?: string;
}

/** First letter of the name, or a neutral placeholder when there is no name. */
function initial(name?: string | null) {
  const ch = name?.trim()?.[0];
  return ch ? ch.toUpperCase() : '?';
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  // Keyed by src, so a new image starts hidden without an effect resetting it.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && failedSrc !== src;
  const loaded = showImage && loadedSrc === src;

  return (
    <span
      className={slots.base({
        className: cn('shrink-0 rounded-full', PX[size], className),
      })}
    >
      <span
        aria-hidden={loaded || undefined}
        className={slots.fallback({
          className: 'bg-primary-50 font-semibold text-primary-600',
        })}
      >
        {initial(name)}
      </span>
      {showImage && (
        <img
          src={src}
          alt={name ?? ''}
          className={slots.image({
            className: loaded ? 'opacity-100' : 'opacity-0',
          })}
          onLoad={() => setLoadedSrc(src)}
          onError={() => setFailedSrc(src)}
        />
      )}
    </span>
  );
}
